import type {
  InventoryQuery,
  InvoicesQuery,
  OrdersQuery,
  SalesReportQuery,
} from '@controlstock/contracts';
import type {
  MatchingQuery,
  MovementsQuery,
  ReconciliationHistoryQuery,
  SyncJobsQuery,
  UsersQuery,
} from '@/lib/api-types';

/**
 * A single, namespaced query-key factory makes targeted invalidation safe after
 * inventory mutations and avoids stringly-typed cache keys in feature screens.
 */
export const queryKeys = {
  root: ['controlstock'] as const,

  auth: {
    all: () => ['controlstock', 'auth'] as const,
    session: () => ['controlstock', 'auth', 'session'] as const,
  },

  dashboard: {
    all: () => ['controlstock', 'dashboard'] as const,
    detail: () => ['controlstock', 'dashboard', 'detail'] as const,
    orders: (query: OrdersQuery) => ['controlstock', 'dashboard', 'orders', query] as const,
    invoices: (query: InvoicesQuery) => ['controlstock', 'dashboard', 'invoices', query] as const,
  },

  onboarding: {
    all: () => ['controlstock', 'onboarding'] as const,
    detail: () => ['controlstock', 'onboarding', 'detail'] as const,
  },

  settings: {
    all: () => ['controlstock', 'settings'] as const,
    arcaInvoicing: () => ['controlstock', 'settings', 'arca-invoicing'] as const,
  },

  inventory: {
    all: () => ['controlstock', 'inventory'] as const,
    syncProgress: () => ['controlstock', 'inventory', 'sync-progress'] as const,
    list: (query: InventoryQuery) => ['controlstock', 'inventory', 'list', query] as const,
  },

  products: {
    all: () => ['controlstock', 'products'] as const,
    list: (query: InventoryQuery) => ['controlstock', 'products', 'list', query] as const,
    detail: (productId: string) => ['controlstock', 'products', 'detail', productId] as const,
  },

  matching: {
    all: () => ['controlstock', 'matching'] as const,
    list: (query: MatchingQuery) => ['controlstock', 'matching', 'list', query] as const,
  },

  integrations: {
    all: () => ['controlstock', 'integrations'] as const,
    list: () => ['controlstock', 'integrations', 'list'] as const,
  },

  jobs: {
    all: () => ['controlstock', 'jobs'] as const,
    list: (query: SyncJobsQuery) => ['controlstock', 'jobs', 'list', query] as const,
  },

  users: {
    all: () => ['controlstock', 'users'] as const,
    list: (query: UsersQuery) => ['controlstock', 'users', 'list', query] as const,
  },

  orders: {
    all: () => ['controlstock', 'orders'] as const,
    list: (query: OrdersQuery) => ['controlstock', 'orders', 'list', query] as const,
  },

  reports: {
    all: () => ['controlstock', 'reports'] as const,
    sales: (query: SalesReportQuery) => ['controlstock', 'reports', 'sales', query] as const,
  },

  invoices: {
    all: () => ['controlstock', 'invoices'] as const,
    list: (query: InvoicesQuery) => ['controlstock', 'invoices', 'list', query] as const,
  },

  movements: {
    all: () => ['controlstock', 'movements'] as const,
    list: (query: MovementsQuery) => ['controlstock', 'movements', 'list', query] as const,
  },

  reconciliation: {
    all: () => ['controlstock', 'reconciliation'] as const,
    history: (query: ReconciliationHistoryQuery) =>
      ['controlstock', 'reconciliation', 'history', query] as const,
  },
} as const;
